import { Classes } from "@blueprintjs/core";
import React from "react";
import styled, { css } from "styled-components";

import { HexTooltip } from "./HexTooltip.js";
import { KeyComboTag, KeyComboTagProps } from "./keyComboTag";

export interface HexKeyComboProps extends KeyComboTagProps {
  showEscKey?: boolean;
  vimMode?: boolean;
  label?: string;
  $color?: "inherit";
}

const StyledHexKeyCombo = styled(KeyComboTag)<HexKeyComboProps>`
  && {
    display: inline-flex;
    gap: 1px;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    text-transform: uppercase;
    transform: translateY(1px);

    color: ${({ $color, theme }) =>
      $color === "inherit" ? "inherit" : theme.fontColor.MUTED};
    font-size: ${({ theme }) => theme.fontSize.SMALL};

    ${({ $color, minimal, theme }) =>
      minimal &&
      css`
        padding: 0 4px;

        border: 1px solid ${theme.borderColor.MUTED};
        border-radius: ${theme.borderRadius};

        > .${Classes.ICON} {
          margin: 0;

          > svg {
            color: ${$color === "inherit" ? "inherit" : theme.iconColor};
          }
        }
      `}

    /* If the first thing in the combo is an icon, we need to move it left by 2px to balance left/right padding due to the icon bounding box*/
    &:first-child {
      > .${Classes.ICON} {
        margin-left: -2px;
      }
    }

    label {
      min-width: 12px;
      text-align: center;
      margin: 0;
      padding: 0 2px;
    }
  }
`;

const StyledHexKeyComboWrapper = styled.div<{
  $color?: "inherit";
}>`
  && {
    display: flex;
    gap: 4px;
    align-items: center;
    position: relative;
    font-size: ${({ theme }) => theme.fontSize.SMALL};
    color: ${({ $color, theme }) =>
      $color === "inherit" ? "inherit" : theme.fontColor.MUTED};

    svg {
      color: inherit;
    }
  }
`;

const KeyComboLabel = styled.div<{
  $color?: "inherit";
}>`
  display: flex;
  padding-right: 4px;
  color: ${({ $color, theme }) =>
    $color === "inherit" ? "inherit" : theme.fontColor.DEFAULT};
`;

const ThenSpan = styled.span`
  font-size: ${({ theme }) => theme.fontSize.EXTRA_SMALL};
`;

export const HexKeyCombo: React.ComponentType<HexKeyComboProps> = React.memo(
  function HexKeyCombo({ $color, label, showEscKey, vimMode, ...props }) {
    if (showEscKey) {
      return (
        <HexTooltip
          autoFocus={false}
          content={`${vimMode ? "Shift+" : ""}Esc brings you to Command mode`}
          enforceFocus={false}
          openOnTargetFocus={false}
        >
          <StyledHexKeyComboWrapper $color={$color}>
            {label && <KeyComboLabel $color={$color}>{label}</KeyComboLabel>}
            <StyledHexKeyCombo
              combo={`${vimMode ? "shift+" : ""}esc`}
              minimal={true}
            />
            <ThenSpan>then</ThenSpan>
            <StyledHexKeyCombo $color={$color} {...props} minimal={true} />
          </StyledHexKeyComboWrapper>
        </HexTooltip>
      );
    }

    if (label) {
      return (
        <StyledHexKeyComboWrapper $color={$color}>
          <KeyComboLabel $color={$color}>{label}</KeyComboLabel>
          <StyledHexKeyCombo $color={$color} {...props} minimal={true} />
        </StyledHexKeyComboWrapper>
      );
    }

    return <StyledHexKeyCombo $color={$color} {...props} minimal={true} />;
  },
);
